
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import PasswordTable from './components/PasswordTable';
import { supabase } from '../../../lib/supabase';

interface FolderPasswordEntry {
  folder_name: string;
  has_password: boolean;
  updated_at: string | null;
  is_protected: boolean;
}

const PROTECTED_FOLDERS: Record<string, boolean> = {
  'Grade 7 Cambridge': true,
  'Grade 8 Cambridge': true,
  'Grade 9 Cambridge': true,
  'Grade 10 Cambridge': true,
  'Grade 11 Cambridge': true,
  'Grade 11 cambridge': true,
  'Grade 12 Cambridge': true,
  'Grade 12 cambridge': true,
  'A Level Cambridge': false,
  'Grade 9 Edexcel': true,
  'Grade 10 Edexcel': true,
  'A Levels': true,
  'IB-AI-SL': true,
  'IB-AI-HL': true,
  'IB-AA-SL': true,
  'IB-AA-HL': true,
  'IB Mathematics': true,
  'AP Calculus': false,
  'SAT': true,
  'Mathematics Topics': true,
  'Maths animation': true,
  'Maths Animation': true,
  'Physics Animation': true,
  'physics animation': true,
  'Cambridge Past Papers and Solutions': true,
  'cambridge past papers and solutions': true,
  'Cambridge Past Papers and solutions': true,
  'Grade 9 ICT': true,
  'General Resources': false,
};

const BULK_PASSWORDS: { folderName: string; password: string }[] = [
  { folderName: 'Grade 7 Cambridge', password: 'grade7cam' },
  { folderName: 'Grade 8 Cambridge', password: 'grade8cam' },
  { folderName: 'Grade 9 Cambridge', password: '0580camg9' },
  { folderName: 'Grade 10 Cambridge', password: '0580G10cam' },
  { folderName: 'Grade 9 Edexcel', password: 'grade9edx' },
  { folderName: 'Grade 10 Edexcel', password: 'grade10edx' },
  { folderName: 'A Levels', password: 'alevels2024' },
  { folderName: 'IB-AI-SL', password: 'ibaisl2024' },
  { folderName: 'IB-AI-HL', password: 'ibaihl2024' },
  { folderName: 'IB-AA-SL', password: 'ibaasl2024' },
  { folderName: 'IB-AA-HL', password: 'ibaahl2024' },
  { folderName: 'SAT', password: 'sat2024' },
  { folderName: 'Mathematics Topics', password: 'mathtopics2024' },
  { folderName: 'Grade 11 Cambridge', password: 'grade11cam' },
  { folderName: 'Grade 11 cambridge', password: 'grade11cam' },
  { folderName: 'Grade 12 Cambridge', password: 'grade12cam' },
  { folderName: 'Grade 12 cambridge', password: 'grade12cam' },
  { folderName: 'IB Mathematics', password: 'ibmaths2024' },
  { folderName: 'Maths Animation', password: 'mathsanim2024' },
  { folderName: 'Maths animation', password: 'mathsanim2024' },
  { folderName: 'Physics Animation', password: 'physicsanim2024' },
  { folderName: 'Cambridge Past Papers and Solutions', password: 'cambpastpapers2024' },
  { folderName: 'Cambridge Past Papers and solutions', password: 'cambpastpapers2024' },
  { folderName: 'Grade 9 ICT', password: 'grade9ict' },
];

export default function AdminPasswordsPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<FolderPasswordEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [bulkSetting, setBulkSetting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentFolder: '' });
  const [bulkDone, setBulkDone] = useState(false);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    if (!supabase) return;

    try {
      setLoading(true);

      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('folder')
        .not('folder', 'is', null);

      if (resourceError) throw resourceError;

      const uniqueFolders = [...new Set(resourceData?.map(r => r.folder) || [])];

      const { data: passwordData, error: passwordError } = await supabase
        .from('folder_passwords')
        .select('folder_name, updated_at');

      if (passwordError) throw passwordError;

      const passwordMap = new Map(
        (passwordData || []).map(p => [p.folder_name, p.updated_at])
      );

      const allFolderNames = new Set([
        ...uniqueFolders,
        ...Object.keys(PROTECTED_FOLDERS),
      ]);

      const entryList: FolderPasswordEntry[] = Array.from(allFolderNames)
        .sort((a, b) => {
          const aProtected = PROTECTED_FOLDERS[a] ?? false;
          const bProtected = PROTECTED_FOLDERS[b] ?? false;
          if (aProtected !== bProtected) return aProtected ? -1 : 1;
          return a.localeCompare(b);
        })
        .map(folderName => ({
          folder_name: folderName,
          has_password: passwordMap.has(folderName),
          updated_at: passwordMap.get(folderName) || null,
          is_protected: PROTECTED_FOLDERS[folderName] ?? false,
        }));

      setEntries(entryList);

      // Check if all 13 protected folders already have passwords
      const allSet = BULK_PASSWORDS.every(bp => passwordMap.has(bp.folderName));
      if (allSet) setBulkDone(true);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      showToast('Failed to load folder data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin())) {
      navigate('/');
      return;
    }
    if (!authLoading && user && isAdmin()) {
      fetchData();
    }
  }, [authLoading, user, navigate, fetchData]);

  const handleBulkSetPasswords = async () => {
    if (!supabase) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      showToast('Please sign in again', 'error');
      return;
    }

    setBulkSetting(true);
    setBulkProgress({ current: 0, total: BULK_PASSWORDS.length, currentFolder: '' });

    let successCount = 0;
    let failedFolders: string[] = [];

    for (let i = 0; i < BULK_PASSWORDS.length; i++) {
      const { folderName, password } = BULK_PASSWORDS[i];
      setBulkProgress({ current: i + 1, total: BULK_PASSWORDS.length, currentFolder: folderName });

      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/set-folder-password`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ folderName, password }),
          }
        );

        if (response.ok) {
          successCount++;
        } else {
          const result = await response.json();
          console.error(`Failed to set password for ${folderName}:`, result.error);
          failedFolders.push(folderName);
        }
      } catch (err) {
        console.error(`Error setting password for ${folderName}:`, err);
        failedFolders.push(folderName);
      }
    }

    setBulkSetting(false);

    if (failedFolders.length === 0) {
      showToast(`All ${successCount} passwords set successfully!`, 'success');
      setBulkDone(true);
    } else {
      showToast(`${successCount} set, ${failedFolders.length} failed: ${failedFolders.join(', ')}`, 'error');
    }

    await fetchData();
  };

  const handleSetPassword = async (folderName: string, password: string) => {
    if (!supabase) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      showToast('Please sign in again', 'error');
      return;
    }

    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/set-folder-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ folderName, password }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      showToast(result.error || 'Failed to set password', 'error');
      throw new Error(result.error);
    }

    showToast(`Password updated for "${folderName}"`, 'success');
    await fetchData();
  };

  const handleRemovePassword = async (folderName: string) => {
    if (!supabase) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      showToast('Please sign in again', 'error');
      return;
    }

    const { error } = await supabase
      .from('folder_passwords')
      .delete()
      .eq('folder_name', folderName);

    if (error) {
      showToast('Failed to remove password', 'error');
      throw error;
    }

    showToast(`Password removed from "${folderName}"`, 'success');
    setBulkDone(false);
    await fetchData();
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin block mb-4"></i>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return null;
  }

  return (
    <>
      <Navigation />
      <main className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/resources" className="hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-folder-line mr-1"></i>Resources
            </Link>
            <i className="ri-arrow-right-s-line text-gray-300"></i>
            <span className="text-gray-900 font-medium">Password Management</span>
          </nav>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-xl text-teal-600"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Password Management</h1>
                <p className="text-sm text-gray-500">Set and manage passwords for protected folders</p>
              </div>
            </div>
          </div>

          {/* Bulk Setup Banner */}
          {!bulkDone && !loading && (
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-password-line text-xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-teal-900 text-sm mb-1">Quick Setup: Set All Passwords</h3>
                  <p className="text-sm text-teal-700 mb-3">
                    Set passwords for all protected folders at once. Passwords will be securely encrypted on the server.
                  </p>

                  {/* Password Preview */}
                  <div className="bg-white/70 rounded-lg border border-teal-100 mb-4 max-h-52 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-teal-100">
                          <th className="text-left py-2 px-3 text-teal-800 font-medium text-xs">Folder</th>
                          <th className="text-left py-2 px-3 text-teal-800 font-medium text-xs">Password</th>
                        </tr>
                      </thead>
                      <tbody>
                        {BULK_PASSWORDS.map((bp, idx) => (
                          <tr key={bp.folderName} className={idx % 2 === 0 ? 'bg-white/50' : ''}>
                            <td className="py-1.5 px-3 text-gray-700 text-xs">{bp.folderName}</td>
                            <td className="py-1.5 px-3 font-mono text-xs text-teal-700">{bp.password}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Progress Bar */}
                  {bulkSetting && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-teal-700 mb-1.5">
                        <span>Setting: {bulkProgress.currentFolder}</span>
                        <span>{bulkProgress.current} / {bulkProgress.total}</span>
                      </div>
                      <div className="w-full bg-teal-100 rounded-full h-2">
                        <div
                          className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleBulkSetPasswords}
                    disabled={bulkSetting}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {bulkSetting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        Setting passwords...
                      </>
                    ) : (
                      <>
                        <i className="ri-shield-check-line text-base"></i>
                        Set All Passwords Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Banner */}
          {bulkDone && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0"></i>
              <div className="text-sm text-green-800">
                <p className="font-medium">All passwords are set and securely encrypted!</p>
                <p className="text-green-600 mt-0.5">You can change individual passwords below anytime.</p>
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <i className="ri-information-line text-teal-600 text-lg mt-0.5 flex-shrink-0"></i>
            <div className="text-sm text-teal-800">
              <p className="font-medium mb-1">How it works</p>
              <p className="text-teal-700">
                Protected folders require a password for non-admin users. Passwords are securely encrypted on the server. 
                As an admin, you can access all folders without passwords.
              </p>
            </div>
          </div>

          {/* Password Table */}
          <PasswordTable
            entries={entries}
            onSetPassword={handleSetPassword}
            onRemovePassword={handleRemovePassword}
            loading={loading}
          />

          {/* Back Link */}
          <div className="mt-8 text-center">
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Resources
            </Link>
          </div>
        </div>
      </main>
      <Footer />

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg shadow-xl flex items-center gap-2 animate-fade-in ${
          toast.type === 'success' ? 'bg-gray-900 text-white' : 'bg-red-600 text-white'
        }`}>
          <i className={`text-lg ${
            toast.type === 'success' ? 'ri-checkbox-circle-line text-green-400' : 'ri-error-warning-line text-red-200'
          }`}></i>
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      )}
    </>
  );
}
